import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api, HTTPError, ErrorResponse } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import type { TPromptType } from 'extra/types/ApiModels'

export function NewPromptPage () {
  const navigate = useNavigate()
  const { refreshPrompts, folders } = useEntities()
  const [name, setName] = useState('')
  const [type, setType] = useState<TPromptType>('completion')
  const [description, setDescription] = useState('')
  const [folderId, setFolderId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const prompt = await api.prompts.create({
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        folderId: folderId || null,
      })
      await refreshPrompts()
      navigate(`/prompts/${prompt.id}`)
    } catch (err) {
      if (err instanceof HTTPError) {
        try {
          const errorData = await err.response.json() as ErrorResponse
          setError(errorData.error || 'Failed to create prompt')
        } catch {
          setError('Failed to create prompt')
        }
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <div className="max-w-xl">
        <h1 className="text-2xl font-bold text-white mb-6">Create New Prompt</h1>

        {error && (
          <div className="alert-error mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="input-label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Enter prompt name"
            />
          </div>

          <div>
            <label htmlFor="type" className="input-label">
              Type
            </label>
            <select
              id="type"
              name="type"
              value={type}
              onChange={(e) => setType(e.target.value as TPromptType)}
              className="input"
            >
              <option value="completion">Completion (chat messages)</option>
              <option value="decision">Decision (classify items into given choices)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Can't be changed later. Decision prompts have no scenarios, datasets or simulations.</p>
          </div>

          <div>
            <label htmlFor="description" className="input-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              rows={3}
              placeholder="Enter prompt description (optional)"
            />
          </div>

          <div>
            <label htmlFor="folder" className="input-label">
              Folder
            </label>
            <select
              id="folder"
              name="folder"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="input"
            >
              <option value="">No folder (Uncategorized)</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="btn btn-primary"
            >
              {isLoading ? 'Creating...' : 'Create Prompt'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
